import React from 'react';
import { Container } from '../components/common/Container';
import { Card } from '../components/common/Card';
import { HomeIcon, BuildingOfficeIcon, BuildingOffice2Icon, CheckIcon } from '@heroicons/react/24/outline';
import { BOOKING_DURATION_MINUTES, BOOKING_URL } from '../config/contact';

const solutions = [
  {
    Icon: HomeIcon,
    title: 'Residential Solutions',
    tagline: 'Perfect for homeowners and residential portfolios',
    description: 'Streamline appeals for single-family homes, multi-family properties, and residential portfolios with tools designed for residential tax professionals.',
    features: [
      'Single-family home valuations',
      'Multi-family property analysis',
      'Residential portfolio management',
      'HOA and community tracking',
      'Comp-based equity analysis',
      'Bulk processing for large portfolios'
    ],
    idealFor: [
      'Boutique appeal firms',
      'Residential portfolio managers',
      'Tax consultants',
      'Property management companies'
    ]
  },
  {
    Icon: BuildingOfficeIcon,
    title: 'Commercial Solutions',
    tagline: 'Built for complex commercial properties',
    description: 'Manage office buildings, retail centers, industrial properties, and mixed-use developments with enterprise-grade valuation and tracking tools.',
    features: [
      'Income approach modeling',
      'Cap rate analysis',
      'Cost approach for improvements',
      'Tenant analysis and lease abstracting',
      'Market rent comparisons',
      'Complex property structures'
    ],
    idealFor: [
      'Commercial appeal specialists',
      'Mid-size regional firms',
      'Tax attorneys',
      'Enterprise clients'
    ]
  },
  {
    Icon: BuildingOffice2Icon,
    title: 'Condominium Solutions',
    tagline: 'Specialized for condo complexes',
    description: 'Purpose-built features for condominium appeals including unit-level analysis, common area allocations, and HOA integration.',
    features: [
      'Unit-level valuations',
      'Common area allocation',
      'HOA fee analysis',
      'Bulk unit processing',
      'Building-wide comparisons',
      'Equity analysis across units'
    ],
    idealFor: [
      'HOA management companies',
      'Condo-focused appeal firms',
      'Multi-family specialists',
      'Property managers'
    ]
  }
];

export const Solutions: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section className="bg-brand-blue py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Solutions for Every Property Type
            </h1>
            <p className="text-xl text-brand-blue-100">
              Whether you specialize in residential, commercial, or condominiums,
              Appeal Advantage has the tools you need to succeed.
            </p>
          </div>
        </Container>
      </section>
      
      {/* Solutions Sections */}
      {solutions.map((solution, index) => (
        <section 
          key={index} 
          className={`py-24 ${index % 2 === 0 ? 'bg-white' : 'bg-brand-blue-50'}`}
        >
          <Container>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="w-16 h-16 bg-brand-red rounded-lg flex items-center justify-center mb-6">
                  <solution.Icon className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-brand-blue mb-3">
                  {solution.title}
                </h2>
                <p className="text-lg text-brand-red font-semibold mb-4">
                  {solution.tagline}
                </p>
                <p className="text-lg text-brand-white-700 mb-6">
                  {solution.description}
                </p>
                
                <h3 className="text-xl font-bold text-brand-blue mb-4">
                  Key Features:
                </h3>
                <ul className="space-y-2">
                  {solution.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <CheckIcon className="w-5 h-5 text-brand-red mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-brand-white-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <Card>
                  <h3 className="text-xl font-bold text-brand-blue mb-4">
                    Ideal For:
                  </h3>
                  <ul className="space-y-3">
                    {solution.idealFor.map((audience, i) => (
                      <li key={i} className="flex items-start">
                        <CheckIcon className="w-5 h-5 text-brand-blue mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-brand-white-700 font-medium">{audience}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-sm text-brand-white-600 mb-4">
                      Want to see how this works for your property type?
                    </p>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block w-full bg-brand-red hover:bg-brand-red-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors text-center"
                    >
                      Book a Workflow Review
                    </a>
                  </div>
                </Card>
              </div>
            </div>
          </Container>
        </section>
      ))}
      
      {/* Final CTA */}
      <section className="py-24 bg-brand-blue">
        <Container>
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              See How Appeal Advantage Fits Your Workflow
            </h2>
            <p className="text-xl text-brand-blue-100 mb-8">
              Bring your current process and the bottleneck you want to improve.
              We’ll focus the review on the property types, stages, and deadlines
              that matter to your team.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-brand-red hover:bg-brand-red-600 text-white font-semibold px-8 py-4 rounded-lg transition-colors"
            >
              Book a {BOOKING_DURATION_MINUTES}-minute workflow review
            </a>
          </div>
        </Container>
      </section>
    </>
  );
};
